import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { actividadAPI, usuariosAPI } from '../services/api';
import { useApp } from '../context/AppContext';

/**
 * ACTIVIDAD DEL EQUIPO — supervisión de lo que hace cada usuario.
 * Notas de venta, ventas de servicios oftalmológicos, abonos y órdenes de trabajo,
 * en una línea de tiempo unificada con filtros y tarjetas por persona.
 */

const TIPOS = {
  venta:    { label: 'Notas de venta',  corto: 'Venta',    icon: 'bi-cart-check-fill',       color: '#0d6efd', bg: 'rgba(13,110,253,.10)' },
  servicio: { label: 'Servicios',       corto: 'Servicio', icon: 'bi-heart-pulse-fill',      color: '#0dcaf0', bg: 'rgba(13,202,240,.12)' },
  abono:    { label: 'Abonos',          corto: 'Abono',    icon: 'bi-cash-coin',             color: '#198754', bg: 'rgba(25,135,84,.10)' },
  orden:    { label: 'Órdenes',         corto: 'Orden',    icon: 'bi-eyeglasses',            color: '#6f42c1', bg: 'rgba(111,66,193,.10)' }
};

const hoyISO = () => new Date().toISOString().split('T')[0];
const diasAtrasISO = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().split('T')[0]; };

const iniciales = (nombre) => (nombre || '?').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();
const colorDe = (id) => ['#0d6efd', '#198754', '#6f42c1', '#fd7e14', '#d63384', '#20c997', '#6610f2'][(id || 0) % 7];

const ActividadEquipo = () => {
  const { formatCurrency } = useApp();

  const [rango, setRango] = useState('hoy');
  const [fechaDesde, setFechaDesde] = useState(hoyISO());
  const [fechaHasta, setFechaHasta] = useState(hoyISO());
  const [usuarioId, setUsuarioId] = useState('');
  const [tiposSel, setTiposSel] = useState(Object.keys(TIPOS));
  const [sriFiltro, setSriFiltro] = useState(''); // '' | 'con' | 'sin'
  const [buscar, setBuscar] = useState('');
  const [buscarDebounced, setBuscarDebounced] = useState('');
  const [exportando, setExportando] = useState(false);

  const [usuarios, setUsuarios] = useState([]);
  const [resumen, setResumen] = useState([]);
  const [feed, setFeed] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loading, setLoading] = useState(true);

  // [Corrección N° de orden] Única escritura del módulo. Solo toca numero_orden.
  const [edicionOrden, setEdicionOrden] = useState(null); // { tipo, ref_id, codigo, actual, valor }
  const [guardandoOrden, setGuardandoOrden] = useState(false);

  const abrirEdicionOrden = (it) => setEdicionOrden({
    tipo: it.tipo, ref_id: it.ref_id, codigo: it.codigo,
    actual: it.numero_orden || '', valor: it.numero_orden || ''
  });

  const guardarNumeroOrden = async () => {
    if (!edicionOrden) return;
    const valor = (edicionOrden.valor || '').trim();
    if (valor.length > 50) { toast.error('El N° de orden no puede exceder 50 caracteres'); return; }
    if (valor === (edicionOrden.actual || '')) { setEdicionOrden(null); return; }

    setGuardandoOrden(true);
    try {
      const res = await actividadAPI.actualizarNumeroOrden({
        tipo: edicionOrden.tipo, ref_id: edicionOrden.ref_id, numero_orden: valor
      });
      if (res.data.success) {
        toast.success(res.data.message || 'N° de orden actualizado');
        const dups = res.data.data?.duplicados || [];
        if (dups.length > 0) {
          toast.warning(`Ojo: el N° ${valor} también está en ${dups.map(d => d.codigo).join(', ')}.`);
        }
        setEdicionOrden(null);
        cargar(pagination.page);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo actualizar el N° de orden');
    }
    setGuardandoOrden(false);
  };

  useEffect(() => {
    const t = setTimeout(() => setBuscarDebounced(buscar), 350);
    return () => clearTimeout(t);
  }, [buscar]);

  useEffect(() => {
    usuariosAPI.getAll({ activo: 'true' })
      .then(r => { if (r.data.success) setUsuarios(r.data.data || []); })
      .catch(() => { /* no bloquea la vista */ });
  }, []);

  const aplicarRango = (r) => {
    setRango(r);
    if (r === 'hoy') { setFechaDesde(hoyISO()); setFechaHasta(hoyISO()); }
    else if (r === '7') { setFechaDesde(diasAtrasISO(6)); setFechaHasta(hoyISO()); }
    else if (r === '30') { setFechaDesde(diasAtrasISO(29)); setFechaHasta(hoyISO()); }
    else if (r === 'mes') { const d = new Date(); setFechaDesde(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]); setFechaHasta(hoyISO()); }
  };

  const cargar = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const base = { fecha_desde: fechaDesde || undefined, fecha_hasta: fechaHasta || undefined };
      const paramsFeed = {
        ...base, page, limit: 30,
        usuario_id: usuarioId || undefined,
        tipos: tiposSel.length && tiposSel.length < 4 ? tiposSel.join(',') : undefined,
        buscar: buscarDebounced || undefined,
        sri: sriFiltro || undefined
      };
      const [feedRes, resRes] = await Promise.all([
        actividadAPI.getFeed(paramsFeed),
        actividadAPI.getResumen(base)
      ]);
      if (feedRes.data.success) { setFeed(feedRes.data.data || []); setPagination(feedRes.data.pagination || { page: 1, totalPages: 1, total: 0 }); }
      if (resRes.data.success) setResumen(resRes.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Error al cargar la actividad del equipo');
    }
    setLoading(false);
  }, [fechaDesde, fechaHasta, usuarioId, tiposSel, buscarDebounced, sriFiltro]);

  useEffect(() => { cargar(1); }, [cargar]);

  const toggleTipo = (t) => {
    setTiposSel(prev => prev.includes(t) ? (prev.length === 1 ? prev : prev.filter(x => x !== t)) : [...prev, t]);
  };

  const kpis = useMemo(() => {
    const acc = { ventas: 0, ventasMonto: 0, servicios: 0, serviciosMonto: 0, abonos: 0, abonosMonto: 0, ordenes: 0 };
    (resumen || []).forEach(u => {
      acc.ventas += u.ventas_count || 0;          acc.ventasMonto += parseFloat(u.ventas_total) || 0;
      acc.servicios += u.servicios_count || 0;    acc.serviciosMonto += parseFloat(u.servicios_total) || 0;
      acc.abonos += u.abonos_count || 0;          acc.abonosMonto += parseFloat(u.abonos_total) || 0;
      acc.ordenes += u.ordenes_count || 0;
    });
    return acc;
  }, [resumen]);

  const activos = useMemo(
    () => (resumen || []).filter(u => (u.ventas_count + u.servicios_count + u.abonos_count + u.ordenes_count) > 0),
    [resumen]
  );
  const maxMonto = useMemo(
    () => Math.max(1, ...activos.map(u => (parseFloat(u.ventas_total) || 0) + (parseFloat(u.servicios_total) || 0))),
    [activos]
  );

  // Agrupar el feed por día para la línea de tiempo
  const feedPorDia = useMemo(() => {
    const grupos = {};
    (feed || []).forEach(item => {
      const dia = item.fecha ? new Date(item.fecha).toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long' }) : 'Sin fecha';
      (grupos[dia] = grupos[dia] || []).push(item);
    });
    return Object.entries(grupos);
  }, [feed]);

  const limpiar = () => {
    setUsuarioId(''); setBuscar(''); setTiposSel(Object.keys(TIPOS)); setSriFiltro(''); aplicarRango('hoy');
  };

  // Descarga del Excel con los MISMOS filtros activos (sin paginar).
  const exportarExcel = async () => {
    setExportando(true);
    try {
      const res = await actividadAPI.exportarExcel({
        fecha_desde: fechaDesde || undefined,
        fecha_hasta: fechaHasta || undefined,
        usuario_id: usuarioId || undefined,
        tipos: tiposSel.length && tiposSel.length < 4 ? tiposSel.join(',') : undefined,
        buscar: buscarDebounced || undefined,
        sri: sriFiltro || undefined
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `actividad-equipo-${fechaDesde || 'inicio'}_${fechaHasta || 'hoy'}.xlsx`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { window.URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
      toast.success('Excel descargado');
    } catch {
      toast.error('Error al generar el Excel');
    }
    setExportando(false);
  };

  const hora = (f) => f ? new Date(f).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) : '—';

  return (
    <div className="fade-in">
      {/* ─── Encabezado ─── */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <div>
          <h4 className="mb-0 fw-bold"><i className="bi bi-people-fill me-2 text-primary"></i>Actividad del Equipo</h4>
          <small className="text-muted">Todo lo que registra cada usuario: notas de venta, servicios, abonos y órdenes</small>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-success btn-sm" onClick={exportarExcel} disabled={exportando || loading}
            title="Descargar el listado filtrado en Excel">
            <i className={`bi ${exportando ? 'bi-hourglass-split' : 'bi-file-earmark-excel'} me-1`}></i>
            {exportando ? 'Generando...' : 'Exportar Excel'}
          </button>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => cargar(pagination.page)} disabled={loading}>
            <i className={`bi ${loading ? 'bi-hourglass-split' : 'bi-arrow-clockwise'} me-1`}></i>Actualizar
          </button>
        </div>
      </div>

      {/* ─── KPIs ─── */}
      <div className="row g-3 mb-3">
        {[
          { k: 'venta', valor: formatCurrency(kpis.ventasMonto), sub: `${kpis.ventas} notas de venta` },
          { k: 'servicio', valor: formatCurrency(kpis.serviciosMonto), sub: `${kpis.servicios} atenciones` },
          { k: 'abono', valor: formatCurrency(kpis.abonosMonto), sub: `${kpis.abonos} abonos cobrados` },
          { k: 'orden', valor: kpis.ordenes, sub: 'órdenes de trabajo' }
        ].map(({ k, valor, sub }) => (
          <div className="col-6 col-lg-3" key={k}>
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${TIPOS[k].color}` }}>
              <div className="card-body py-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <small className="text-muted d-block">{TIPOS[k].label}</small>
                    <h4 className="mb-0 fw-bold" style={{ color: TIPOS[k].color }}>{valor}</h4>
                    <small className="text-muted">{sub}</small>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42, background: TIPOS[k].bg, flexShrink: 0 }}>
                    <i className={`bi ${TIPOS[k].icon} fs-5`} style={{ color: TIPOS[k].color }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ─── Filtros ─── */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-lg-4">
              <label className="form-label small fw-semibold mb-1">Buscar</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text"><i className="bi bi-search"></i></span>
                <input type="text" className="form-control" placeholder="Código, N° orden, paciente o cédula..."
                  value={buscar} onChange={e => setBuscar(e.target.value)} />
              </div>
            </div>
            <div className="col-6 col-lg-3">
              <label className="form-label small fw-semibold mb-1">Usuario</label>
              <select className="form-select form-select-sm" value={usuarioId} onChange={e => setUsuarioId(e.target.value)}>
                <option value="">Todo el equipo</option>
                {usuarios.map(u => <option key={u.id} value={u.id}>{u.nombre_completo}</option>)}
              </select>
            </div>
            <div className="col-6 col-lg-2">
              <label className="form-label small fw-semibold mb-1">Desde</label>
              <input type="date" className="form-control form-control-sm" value={fechaDesde}
                onChange={e => { setFechaDesde(e.target.value); setRango('custom'); }} />
            </div>
            <div className="col-6 col-lg-2">
              <label className="form-label small fw-semibold mb-1">Hasta</label>
              <input type="date" className="form-control form-control-sm" value={fechaHasta}
                onChange={e => { setFechaHasta(e.target.value); setRango('custom'); }} />
            </div>
            <div className="col-6 col-lg-1 d-grid">
              <button className="btn btn-outline-secondary btn-sm" onClick={limpiar} title="Limpiar filtros">
                <i className="bi bi-x-circle"></i>
              </button>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2 mt-3 align-items-center">
            {[{ id: 'hoy', l: 'Hoy' }, { id: '7', l: '7 días' }, { id: '30', l: '30 días' }, { id: 'mes', l: 'Este mes' }].map(r => (
              <button key={r.id} className={`btn btn-sm ${rango === r.id ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => aplicarRango(r.id)}>{r.l}</button>
            ))}
            <span className="vr mx-1 d-none d-lg-inline"></span>
            {Object.entries(TIPOS).map(([k, t]) => {
              const on = tiposSel.includes(k);
              return (
                <button key={k} className="btn btn-sm" onClick={() => toggleTipo(k)}
                  style={{
                    background: on ? t.color : 'transparent', color: on ? '#fff' : t.color,
                    border: `1px solid ${t.color}`, fontWeight: 600
                  }}>
                  <i className={`bi ${t.icon} me-1`}></i>{t.label}
                </button>
              );
            })}
          </div>

          {/* Filtro de facturación SRI (aplica a notas de venta) */}
          <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
            <span className="small fw-semibold text-muted">
              <i className="bi bi-receipt me-1"></i>Facturación:
            </span>
            {[
              { v: '', l: 'Todas', icon: 'bi-list-ul', color: '#6c757d' },
              { v: 'con', l: 'CON SRI', icon: 'bi-file-earmark-check-fill', color: '#198754' },
              { v: 'sin', l: 'SIN SRI', icon: 'bi-file-earmark-x', color: '#fd7e14' }
            ].map(o => {
              const on = sriFiltro === o.v;
              return (
                <button key={o.v || 'todas'} className="btn btn-sm" onClick={() => setSriFiltro(o.v)}
                  style={{
                    background: on ? o.color : 'transparent', color: on ? '#fff' : o.color,
                    border: `1px solid ${o.color}`, fontWeight: 600
                  }}>
                  <i className={`bi ${o.icon} me-1`}></i>{o.l}
                </button>
              );
            })}
            {sriFiltro && (
              <small className="text-muted fst-italic">
                Mostrando solo notas de venta (los servicios usan comprobante interno)
              </small>
            )}
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* ─── Tarjetas por usuario ─── */}
        <div className="col-12 col-xl-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 pt-3 pb-2">
              <h6 className="mb-0 fw-bold"><i className="bi bi-trophy-fill me-2 text-warning"></i>Rendimiento por persona</h6>
              <small className="text-muted">En el período seleccionado</small>
            </div>
            <div className="card-body pt-2" style={{ maxHeight: 620, overflowY: 'auto' }}>
              {activos.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-inbox fs-1 d-block opacity-25 mb-2"></i>
                  Sin actividad registrada
                </div>
              ) : activos.map(u => {
                const monto = (parseFloat(u.ventas_total) || 0) + (parseFloat(u.servicios_total) || 0);
                const pct = Math.round((monto / maxMonto) * 100);
                const activo = String(usuarioId) === String(u.id);
                return (
                  <div key={u.id}
                    onClick={() => setUsuarioId(activo ? '' : String(u.id))}
                    className={`p-2 mb-2 rounded-3 ${activo ? 'border border-primary' : 'border'}`}
                    style={{ cursor: 'pointer', background: activo ? 'rgba(13,110,253,.04)' : '#fff' }}
                    title="Clic para filtrar por esta persona">
                    <div className="d-flex align-items-center gap-2">
                      <div className="rounded-circle text-white d-flex align-items-center justify-content-center fw-bold"
                        style={{ width: 38, height: 38, background: colorDe(u.id), fontSize: '.8rem', flexShrink: 0 }}>
                        {iniciales(u.nombre_completo)}
                      </div>
                      <div className="flex-grow-1 min-w-0">
                        <div className="fw-bold text-truncate" style={{ fontSize: '.9rem' }}>{u.nombre_completo}</div>
                        <small className="text-muted text-capitalize">{u.rol || 'usuario'}</small>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold text-success">{formatCurrency(monto)}</div>
                      </div>
                    </div>
                    <div className="progress mt-2" style={{ height: 5 }}>
                      <div className="progress-bar" style={{ width: `${pct}%`, background: colorDe(u.id) }}></div>
                    </div>
                    <div className="d-flex flex-wrap gap-1 mt-2">
                      {u.ventas_count > 0 && <span className="badge rounded-pill" style={{ background: TIPOS.venta.bg, color: TIPOS.venta.color }}>{u.ventas_count} ventas</span>}
                      {u.servicios_count > 0 && <span className="badge rounded-pill" style={{ background: TIPOS.servicio.bg, color: '#0aa2c0' }}>{u.servicios_count} serv.</span>}
                      {u.abonos_count > 0 && <span className="badge rounded-pill" style={{ background: TIPOS.abono.bg, color: TIPOS.abono.color }}>{u.abonos_count} abonos</span>}
                      {u.ordenes_count > 0 && <span className="badge rounded-pill" style={{ background: TIPOS.orden.bg, color: TIPOS.orden.color }}>{u.ordenes_count} órdenes</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ─── Línea de tiempo ─── */}
        <div className="col-12 col-xl-8">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-header bg-white border-0 pt-3 pb-2 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0 fw-bold"><i className="bi bi-clock-history me-2 text-primary"></i>Línea de tiempo</h6>
                <small className="text-muted">{pagination.total} movimiento{pagination.total === 1 ? '' : 's'}
                  {usuarioId && usuarios.find(u => String(u.id) === String(usuarioId))
                    ? ` · ${usuarios.find(u => String(u.id) === String(usuarioId)).nombre_completo}` : ''}
                </small>
              </div>
            </div>
            <div className="card-body pt-2">
              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
              ) : feed.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-calendar-x fs-1 d-block opacity-25 mb-2"></i>
                  <div className="fw-semibold">Sin movimientos en este período</div>
                  <small>Prueba ampliando el rango de fechas o quitando filtros</small>
                </div>
              ) : (
                <div style={{ maxHeight: 560, overflowY: 'auto' }}>
                  {feedPorDia.map(([dia, items]) => (
                    <div key={dia} className="mb-3">
                      <div className="text-uppercase fw-bold text-muted mb-2 sticky-top bg-white py-1"
                        style={{ fontSize: '.7rem', letterSpacing: '.5px' }}>{dia}</div>
                      {items.map((it, i) => {
                        const t = TIPOS[it.tipo] || TIPOS.venta;
                        return (
                          <div key={`${it.tipo}-${it.ref_id}-${i}`} className="d-flex gap-2 py-2 border-bottom align-items-center">
                            <div className="rounded d-flex align-items-center justify-content-center"
                              style={{ width: 36, height: 36, background: t.bg, flexShrink: 0 }}>
                              <i className={`bi ${t.icon}`} style={{ color: t.color }}></i>
                            </div>
                            <div className="flex-grow-1 min-w-0">
                              <div className="d-flex flex-wrap align-items-center gap-1">
                                <span className="badge" style={{ background: t.color }}>{t.corto}</span>
                                <span className="fw-bold">{it.codigo || '—'}</span>
                                {it.numero_orden && <span className="badge bg-light text-primary fw-bold">Orden: {it.numero_orden}</span>}
                                {it.etiqueta && <span className="badge bg-light text-dark">{it.etiqueta}</span>}
                                {/* Corregir N° de orden física. Solo aplica a registros que lo
                                    tienen: los abonos no llevan numero_orden propio. */}
                                {it.tipo !== 'abono' && (
                                  <button type="button"
                                    className="btn btn-link btn-sm p-0 ms-1 text-secondary"
                                    style={{ fontSize: '0.75rem', textDecoration: 'none' }}
                                    title={it.numero_orden ? 'Corregir el N° de orden física' : 'Asignar N° de orden física'}
                                    onClick={() => abrirEdicionOrden(it)}>
                                    <i className="bi bi-pencil-square"></i>
                                  </button>
                                )}
                              </div>
                              <div className="small text-muted text-truncate">
                                <i className="bi bi-person me-1"></i>{it.cliente}
                                <span className="mx-1">·</span>
                                <i className="bi bi-person-badge me-1"></i>{it.usuario}
                                {it.metodo && <><span className="mx-1">·</span>{it.metodo}</>}
                              </div>
                            </div>
                            <div className="text-end" style={{ flexShrink: 0 }}>
                              {it.monto !== null && it.monto !== undefined && (
                                <div className="fw-bold" style={{ color: t.color }}>{formatCurrency(it.monto)}</div>
                              )}
                              <small className="text-muted">{hora(it.fecha)}</small>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center pt-2 border-top mt-2">
                  <button className="btn btn-sm btn-outline-secondary" disabled={pagination.page <= 1}
                    onClick={() => cargar(pagination.page - 1)}>Anterior</button>
                  <small className="text-muted">Página {pagination.page} de {pagination.totalPages}</small>
                  <button className="btn btn-sm btn-outline-secondary" disabled={pagination.page >= pagination.totalPages}
                    onClick={() => cargar(pagination.page + 1)}>Siguiente</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* [Corrección N° de orden física] Modal acotado: solo este campo. */}
      {edicionOrden && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.5)' }} onClick={() => !guardandoOrden && setEdicionOrden(null)}>
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-hash me-2"></i>Corregir N° de orden física</h5>
                <button type="button" className="btn-close" disabled={guardandoOrden} onClick={() => setEdicionOrden(null)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-light border small mb-3">
                  <div><strong>Registro:</strong> {edicionOrden.codigo}</div>
                  <div><strong>N° de orden actual:</strong> {edicionOrden.actual || <span className="text-muted">(sin asignar)</span>}</div>
                </div>

                <label className="form-label small fw-semibold">Nuevo N° de orden física</label>
                <input type="text" className="form-control" maxLength={50} autoFocus
                  value={edicionOrden.valor}
                  placeholder="Código de la orden de papel"
                  onChange={e => setEdicionOrden({ ...edicionOrden, valor: e.target.value })}
                  onKeyDown={e => { if (e.key === 'Enter' && !guardandoOrden) guardarNumeroOrden(); }} />
                <div className="form-text">
                  Déjalo <strong>vacío</strong> para quitar la orden de este registro (útil si se tecleó en el cliente equivocado).
                </div>

                <div className="alert alert-warning small mt-3 mb-0">
                  <i className="bi bi-shield-check me-1"></i>
                  Solo se modifica el N° de orden. <strong>No cambia el cliente, ni los montos, ni la facturación.</strong>
                  El cambio queda registrado con tu usuario, fecha y valor anterior.
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" disabled={guardandoOrden} onClick={() => setEdicionOrden(null)}>Cancelar</button>
                <button className="btn btn-primary" disabled={guardandoOrden} onClick={guardarNumeroOrden}>
                  {guardandoOrden
                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Guardando...</>
                    : <><i className="bi bi-check-lg me-1"></i>Guardar</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActividadEquipo;
